import React, { useState, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { FileSpreadsheet } from 'lucide-react';
import { FullScreenLayout } from '../../routes/layouts/FullScreenLayout';
import { FullScreenHeader } from '@/components/shared/layout';
import { Button } from '@/components/shared/ui';
import { useAuth } from '../../contexts/AuthContext';
import { useClassDetail } from '@/data/queries/useClasses';
import { classDetailDtoToClassItem } from '@/utils/classUtils';
import { useParticipantStats } from '@/data/queries/useParticipantStats';
import { useScenarioStats } from '@/data/queries/useScenarioStats';
import { downloadStatisticsExcel } from '@/services/classLabPlatformService';

type TabType = 'curriculum' | 'evaluation' | 'practice-status' | 'ranking' | 'statistics';
type StatisticsSubTab = 'participants' | 'scenarios';

/**
 * 밀리초를 시간 형식으로 변환 (예: 111000ms → "1분 51초")
 */
function formatPlayTime(milliseconds: number | null | undefined): string {
    if (milliseconds == null || milliseconds === 0) return '0초';
    
    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    
    const parts = [];
    if (hours > 0) parts.push(`${hours}시간`);
    if (minutes > 0) parts.push(`${minutes}분`);
    if (secs > 0 || parts.length === 0) parts.push(`${secs}초`);
    
    return parts.join(' ');
}

export default function PracticeStatistics(): React.ReactElement {
    const { classId: classIdParam } = useParams<{ classId: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { getCurrentRole } = useAuth();

    const role = getCurrentRole();
    const basePath = role === 'master' ? '/master' : '/admin';
    const classId = parseInt((classIdParam ?? '').trim(), 10);
    const currentTab = searchParams.get('tab') || 'statistics';

    const [statisticsTab, setStatisticsTab] = useState<StatisticsSubTab>('participants');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);

    const {
        data: classDetailDto,
        isError: isClassError,
        isLoading: isClassLoading,
    } = useClassDetail(classId, {
        enabled: classId > 0,
    });

    const classItem = classDetailDto ? classDetailDtoToClassItem(classDetailDto) : null;

    // 참가자 통계 데이터 조회
    const {
        data: participantStatsData,
        isError: isParticipantStatsError,
        isLoading: isParticipantStatsLoading,
    } = useParticipantStats(classId, {
        enabled: classId > 0,
    });

    // 시나리오 통계 데이터 조회
    const {
        data: scenarioStatsData,
        isError: isScenarioStatsError,
        isLoading: isScenarioStatsLoading,
    } = useScenarioStats(classId, {
        enabled: classId > 0 && statisticsTab === 'scenarios',
    });

    const backToClassDetailPath = (): string => {
        return `${basePath}/class-management/${classId}`;
    };

    // 날짜 필터링된 참가자 목록
    const filteredParticipants = useMemo(() => {
        if (!participantStatsData?.userList) return [];
        
        // 날짜 필터는 추후 API에서 지원 예정
        // 현재는 전체 데이터 사용
        return participantStatsData.userList;
    }, [participantStatsData, startDate, endDate]);

    // 통계 카드 데이터
    const stats = useMemo(() => {
        if (!participantStatsData?.stats) {
            return {
                totalParticipants: 0,
                passCount: 0,
                passRate: 0,
                totalPlayCount: 0,
                totalPlayTime: '0시간 0분 0초',
            };
        }

        const { total_participants, pass_count, total_play_count, total_play_time } = participantStatsData.stats;
        const passRate = total_participants > 0 ? Math.round((pass_count / total_participants) * 100) : 0;

        return {
            totalParticipants: total_participants,
            passCount: pass_count,
            passRate,
            totalPlayCount: total_play_count,
            totalPlayTime: formatPlayTime(total_play_time),
        };
    }, [participantStatsData]);

    const handleTabChange = (tab: TabType) => {
        if (tab === 'curriculum') {
            if (classItem?.curriculum?.length) {
                const firstSession = classItem.curriculum[0];
                navigate(`${basePath}/class-management/${classId}/curriculum/${firstSession.id}`);
            }
        } else if (tab === 'evaluation') {
            navigate(`${basePath}/class-management/${classId}/evaluation`);
        } else if (tab === 'practice-status') {
            navigate(`${basePath}/class-management/${classId}/practice-status`);
        } else if (tab === 'statistics') {
            return;
        } else if (tab === 'ranking') {
            navigate(`${basePath}/class-management/${classId}/ranking`);
        } else {
            navigate(`${basePath}/class-management/${classId}/session-management?tab=${tab}`);
        }
    };

    const handleExcelDownload = async () => {
        if (isDownloadingExcel || !classItem) return;

        try {
            setIsDownloadingExcel(true);

            // 파일명 생성: 실습통계_클래스명_2026-08-13.xlsx
            const safeClassName = classItem.title.replace(/[/\\?%*:|"<>]/g, '_').slice(0, 40);
            const timestamp = new Date().toISOString().slice(0, 10);
            const filename = `실습통계_${safeClassName}_${timestamp}.xlsx`;

            await downloadStatisticsExcel(classId, filename);
        } catch (error) {
            console.error('엑셀 다운로드 중 오류:', error);
            alert('엑셀 다운로드에 실패했습니다. 다시 시도해주세요.');
        } finally {
            setIsDownloadingExcel(false);
        }
    };

    const tabs = [
        { id: 'curriculum', label: '커리큘럼' },
        { id: 'evaluation', label: '평가결과' },
        { id: 'practice-status', label: '실습현황' },
        { id: 'ranking', label: '랭킹' },
        { id: 'statistics', label: '실습통계' },
    ];

    const renderCenterContent = () => {
        return (
            <div className="flex gap-2">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => handleTabChange(tab.id as TabType)}
                        className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            currentTab === tab.id
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>
        );
    };

    const renderRightContent = () => {
        return (
            <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-600">조회기간</span>
                <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="px-2 py-1 border border-gray-300 rounded text-xs w-32"
                />
                <span className="text-gray-600">~</span>
                <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="px-2 py-1 border border-gray-300 rounded text-xs w-32"
                />
            </div>
        );
    };

    if (isClassError || isClassLoading || !classItem) {
        return (
            <FullScreenLayout
                header={
                    <FullScreenHeader
                        title={classItem?.title || '실습통계'}
                        onBack={() => navigate(backToClassDetailPath())}
                        centerContent={renderCenterContent()}
                    />
                }
            >
                <div className="flex items-center justify-center h-full text-gray-500">
                    {isClassLoading ? '로딩 중...' : '데이터를 불러올 수 없습니다'}
                </div>
            </FullScreenLayout>
        );
    }

    return (
        <FullScreenLayout
            header={
                <FullScreenHeader
                    title={classItem.title}
                    onBack={() => navigate(backToClassDetailPath())}
                    centerContent={renderCenterContent()}
                    rightContent={renderRightContent()}
                />
            }
        >
            <div className="flex-1 bg-white overflow-y-auto">
                <div className="max-w-[1200px] mx-auto px-12 py-8">
                    {/* 헤더 */}
                    <div className="flex items-center justify-between mb-6">
                        <h1 className="text-2xl font-bold text-gray-900">실습통계</h1>
                        <Button
                            variant="outline"
                            size="sm"
                            className="gap-2"
                            onClick={handleExcelDownload}
                            disabled={isDownloadingExcel}
                        >
                            <FileSpreadsheet size={16} className="text-blue-500" />
                            {isDownloadingExcel ? '다운로드 중...' : '엑셀 다운로드'}
                        </Button>
                    </div>

                    {/* 통계 카드 */}
                    <div className="grid grid-cols-4 gap-4 mb-6">
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                            <div className="text-sm text-gray-600 mb-2">전체 참가자수</div>
                            <div className="text-2xl font-bold text-gray-900">{stats.totalParticipants}명</div>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                            <div className="text-sm text-gray-600 mb-2">PASS</div>
                            <div className="text-2xl font-bold text-gray-900">
                                {stats.passCount}명 ({stats.passRate}%)
                            </div>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                            <div className="text-sm text-gray-600 mb-2">총 실습횟수</div>
                            <div className="text-2xl font-bold text-gray-900">{stats.totalPlayCount}회</div>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                            <div className="text-sm text-gray-600 mb-2">총 실습시간</div>
                            <div className="text-2xl font-bold text-gray-900">{stats.totalPlayTime}</div>
                        </div>
                    </div>

                    {/* 서브 탭 */}
                    <div className="flex gap-2 mb-4">
                        <button
                            onClick={() => setStatisticsTab('participants')}
                            className={`px-4 py-2 text-sm font-medium transition-colors ${
                                statisticsTab === 'participants'
                                    ? 'text-blue-600 border-b-2 border-blue-600'
                                    : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                            참가자
                        </button>
                        <button
                            onClick={() => setStatisticsTab('scenarios')}
                            className={`px-4 py-2 text-sm font-medium transition-colors ${
                                statisticsTab === 'scenarios'
                                    ? 'text-blue-600 border-b-2 border-blue-600'
                                    : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                            시나리오
                        </button>
                    </div>

                    {/* 테이블 */}
                    {statisticsTab === 'participants' && (
                        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                            <table className="w-full">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                                            참가자명 (아이디)
                                        </th>
                                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                            소속
                                        </th>
                                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                            직함
                                        </th>
                                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                            실습횟수
                                        </th>
                                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                            실습시간
                                        </th>
                                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                            참여/완료 시나리오
                                        </th>
                                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                            최고점수
                                        </th>
                                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                            상태
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isParticipantStatsLoading ? (
                                        <tr>
                                            <td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-500">
                                                로딩 중...
                                            </td>
                                        </tr>
                                    ) : isParticipantStatsError ? (
                                        <tr>
                                            <td colSpan={8} className="px-4 py-8 text-center text-sm text-red-500">
                                                데이터를 불러올 수 없습니다
                                            </td>
                                        </tr>
                                    ) : filteredParticipants.length === 0 ? (
                                        <tr>
                                            <td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-500">
                                                참가자 데이터가 없습니다
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredParticipants.map((participant, index) => (
                                            <tr
                                                key={index}
                                                className="border-b border-gray-100 hover:bg-gray-50"
                                            >
                                                <td className="px-4 py-3 text-sm text-gray-900">
                                                    {participant.user_name} ({participant.user_email})
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                                    {participant.affiliation || '—'}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                                    {participant.job_title || '—'}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                                    {participant.play_count}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                                    {formatPlayTime(participant.total_play_time)}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                                    {participant.participated_scenario_count} / {participant.completed_scenario_count}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                                    {participant.highest_score ?? '—'}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                                    {participant.status || '—'}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {statisticsTab === 'scenarios' && (
                        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                            <table className="w-full">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                                            시나리오
                                        </th>
                                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                            모드
                                        </th>
                                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                            실습횟수
                                        </th>
                                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                            실습시간
                                        </th>
                                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                            실습참여자수
                                        </th>
                                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                            완료율
                                        </th>
                                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                            평균점수
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isScenarioStatsLoading ? (
                                        <tr>
                                            <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">
                                                로딩 중...
                                            </td>
                                        </tr>
                                    ) : isScenarioStatsError ? (
                                        <tr>
                                            <td colSpan={7} className="px-4 py-8 text-center text-sm text-red-500">
                                                데이터를 불러올 수 없습니다
                                            </td>
                                        </tr>
                                    ) : !scenarioStatsData?.contentList || scenarioStatsData.contentList.length === 0 ? (
                                        <tr>
                                            <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">
                                                시나리오 데이터가 없습니다
                                            </td>
                                        </tr>
                                    ) : (
                                        scenarioStatsData.contentList.map((scenario, index) => (
                                            <tr
                                                key={index}
                                                className="border-b border-gray-100 hover:bg-gray-50"
                                            >
                                                <td className="px-4 py-3 text-sm text-gray-900">
                                                    {scenario.scenario_name}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                                    {scenario.mode_value_title || '—'}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                                    {scenario.play_count}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                                    {formatPlayTime(scenario.total_play_time)}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                                    {scenario.participant_count}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                                    {scenario.completion_rate ?? 0}%
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                                    {scenario.avg_score ?? 0}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </FullScreenLayout>
    );
}
